import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { AccessMode } from '@/api/bol';
import { useMembers } from '@/hooks/useMembers';

interface ACLAuthorizationCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (memberIdentifier: string, mode: AccessMode, authorizedBy: string) => Promise<void>;
  controlMemberIdentifiers: string[];
}

const ACCESS_MODES: AccessMode[] = ['READ', 'WRITE', 'CONTROL'];

const ACLAuthorizationCreateDialog: React.FC<ACLAuthorizationCreateDialogProps> = ({
  open,
  onClose,
  onSubmit,
  controlMemberIdentifiers
}) => {
  const { members, membersLoading, filteredMembers, fetchMembers } = useMembers();
  const [memberIdentifier, setMemberIdentifier] = useState('');
  const [mode, setMode] = useState<AccessMode>('READ');
  const [authorizedBy, setAuthorizedBy] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authorizedByMembers = useMemo(
    () => filteredMembers
      .filter((member) => !member.disabled)
      .filter((member) => controlMemberIdentifiers.includes(member.identifier)),
    [filteredMembers, controlMemberIdentifiers]
  );

  // Fetch members when dialog opens and members are not loaded
  useEffect(() => {
    if (open && members.length === 0) {
      fetchMembers();
    }
  }, [open, members.length, fetchMembers]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setMemberIdentifier('');
      setMode('READ');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setAuthorizedBy(authorizedByMembers[0]?.identifier || '');
    }
  }, [open, authorizedByMembers]);

  const handleSubmit = async () => {
    setError(null);

    if (!memberIdentifier.trim() || !mode || !authorizedBy) {
      setError('All fields are required');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(memberIdentifier, mode, authorizedBy);
      handleClose();
    } catch (err) {
      console.error('Failed to create ACL authorization:', err);
      setError(err instanceof Error ? err.message : 'Failed to create authorization');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={submitting}
    >
      <DialogTitle>Add ACL Authorization</DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth required>
            <InputLabel id="member-identifier-label">Member Identifier</InputLabel>
            <Select
              labelId="member-identifier-label"
              value={memberIdentifier}
              onChange={(e: SelectChangeEvent<string>) => setMemberIdentifier(e.target.value)}
              label="Member Identifier"
              disabled={submitting || membersLoading}
              endAdornment={
                <IconButton onClick={fetchMembers} disabled={membersLoading || submitting} sx={{ mr: 3 }}>
                  <Tooltip title="Refresh members">
                    <Refresh />
                  </Tooltip>
                </IconButton>
              }
            >
              {membersLoading && <MenuItem disabled>Loading members...</MenuItem>}
              {!membersLoading && members.length === 0 && <MenuItem disabled>No members available</MenuItem>}
              {!membersLoading && members.length > 0 && members.map((member) => (
                <MenuItem 
                  key={member.identifier} 
                  value={member.identifier}
                >
                  {member.identifier}
                </MenuItem>
              ))}
            </Select>
            {!error && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                Select any member to grant access to this logistics object.
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel id="access-mode-label">Access Mode</InputLabel>
            <Select
              labelId="access-mode-label"
              value={mode}
              onChange={(e: SelectChangeEvent<AccessMode>) => setMode(e.target.value as AccessMode)}
              label="Access Mode"
              disabled={submitting}
            >
              {ACCESS_MODES.map((accessMode) => (
                <MenuItem key={accessMode} value={accessMode}>
                  {accessMode}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel id="authorized-by-label">Authorized By (Your Member Identity)</InputLabel>
            <Select
              labelId="authorized-by-label"
              value={authorizedBy}
              onChange={(e: SelectChangeEvent<string>) => setAuthorizedBy(e.target.value)}
              label="Authorized By (Your Member Identity)"
              disabled={submitting || membersLoading}
              endAdornment={
                <IconButton onClick={fetchMembers} disabled={membersLoading || submitting} sx={{ mr: 3 }}>
                  <Tooltip title="Refresh members">
                    <Refresh />
                  </Tooltip>
                </IconButton>
              }
            >
              {membersLoading && <MenuItem disabled>Loading members...</MenuItem>}
              {!membersLoading && authorizedByMembers.length === 0 && (
                <MenuItem disabled>No members available</MenuItem>
              )}
              {!membersLoading && authorizedByMembers.length > 0 && authorizedByMembers.map((member) => (
                <MenuItem 
                  key={member.identifier} 
                  value={member.identifier}
                >
                  {member.identifier}
                </MenuItem>
              ))}
            </Select>
            {!error && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                Only members you can act as, with CONTROL on this object, are available.
              </Typography>
            )}
          </FormControl>

          <Alert severity="info">
            <strong>READ:</strong> Can read the logistics object<br />
            <strong>WRITE:</strong> Can read and propose modifications<br />
            <strong>CONTROL:</strong> Can read, write, and manage ACL
          </Alert>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? <CircularProgress size={24} /> : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ACLAuthorizationCreateDialog;
